import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { firestore } from "../shared/FirebaseConfig";
import {
  doc,
  collection,
  serverTimestamp,
  runTransaction,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import useFetchClasses from "../hooks/useTeacherClasses";
import useFilteredStudents from "../hooks/useFilteredStudents";
import { avatarColor, initials } from "../shared/avatar";
import { useUser } from "../shared/UserContext";
import { useClassroomMode } from "../shared/classroomMode";
import { studentGrade } from "../shared/gradeModel";
import { byLastName } from "../shared/nameSort";
import { lunchSummary } from "../shared/lunchModel";
import SchoolAbsentView from "./SchoolAbsentView";
import LunchCounts from "./LunchCounts";
import LunchPicker from "./LunchPicker";
import "./Attendance.css";

// Local-date helper (avoids UTC rollover issues)
function todayKeyLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const Attendance = () => {
  const location = useLocation();
  const [selectedClassId, setSelectedClassId] = useState("");
  // Open straight to the lunch-counts view when a "🍽 Lunch Count" shortcut sent us
  // here (nav state). Otherwise the plain class roster. modes: class | absent | lunch
  const [mode, setMode] = useState(() => (location.state?.mode === "lunch" ? "lunch" : "class"));

  const { role, schoolId, absentBoardDefault, hideSchoolWideAttendance, lunchEnabled, lunchEntrees, lunchDrinks } = useUser();
  const { classroomMode } = useClassroomMode();
  const isAdmin = role === "admin";
  const showAdminToggle = isAdmin && !classroomMode;
  // Lunch counts are available to teachers (their own classes) and admins
  // (school-wide), but never in the locked classroom view.
  const showLunchMode = lunchEnabled && !classroomMode;
  const showModeToggle = !classroomMode && (isAdmin || showLunchMode);
  // In classroom mode, always fall back to the plain class roster regardless of
  // any mode left selected before the device was locked.
  const effectiveMode = classroomMode ? "class" : mode;
  const [lunchPopup, setLunchPopup] = useState(null); // { student, editing, initial }

  const { classList: allClasses } = useFetchClasses();
  // "Show only my classes" (hideSchoolWideAttendance) must also drop the whole-school
  // classes that SPECIALIST mode injects — otherwise a specialist admin who enabled
  // the toggle still sees every room here, since specialist school-wide classes come
  // in through useFetchClasses, not the admin showSchoolWide query below.
  const ownClasses = allClasses.filter(
    (c) => c.attendanceEnabled !== false && (!hideSchoolWideAttendance || c.isMine),
  );

  // Admins can take attendance for ANY class (rules allow same-school), so they also
  // get the rest of the school's classes — their own first, then the rest A–Z.
  // Unless the admin has opted to hide the school-wide list (a clutter pref) — then
  // we skip the school-wide read entirely and the picker shows only their classes.
  const showSchoolWide = isAdmin && !hideSchoolWideAttendance;
  const [schoolClasses, setSchoolClasses] = useState([]);
  useEffect(() => {
    if (!showSchoolWide || !schoolId) { setSchoolClasses([]); return; }
    return onSnapshot(
      query(collection(firestore, "classes"), where("schoolId", "==", schoolId)),
      snap => setSchoolClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error("[Attendance] school classes:", err),
    );
  }, [showSchoolWide, schoolId]);

  const otherClasses = useMemo(() => {
    if (!showSchoolWide) return [];
    const ownIds = new Set(ownClasses.map(c => c.id));
    return schoolClasses.filter(c => !ownIds.has(c.id)).sort((a, b) => (a.className || "").localeCompare(b.className || ""));
  }, [showSchoolWide, schoolClasses, ownClasses]);

  const dropdownClasses = useMemo(() => [...ownClasses, ...otherClasses], [ownClasses, otherClasses]);
  const { notPresentStudents, presentStudents, refreshFilteredStudents } =
    useFilteredStudents(selectedClassId);

  // Select the first available class on load.
  useEffect(() => {
    if (!selectedClassId && dropdownClasses.length > 0) {
      setSelectedClassId(dropdownClasses[0].id);
    }
  }, [dropdownClasses, selectedClassId]);

  // Admin preference: start on the school-wide absent board.
  useEffect(() => {
    if (showAdminToggle && absentBoardDefault) setMode("absent");
  }, [showAdminToggle, absentBoardDefault]);

  const handleClassChange = (event) => {
    const selectedId = event.target.value;
    setSelectedClassId(selectedId);
  };

  const markAttendance = async (sid) => {
    if (!selectedClassId) return;

    const currentDate = todayKeyLocal();
    const attendanceDocRef = doc(
      firestore,
      "classes",
      selectedClassId,
      "attendance",
      currentDate,
    );
    const studentAttendanceDocRef = doc(
      collection(attendanceDocRef, "students"),
      sid,
    );

    try {
      // Atomic toggle based on real DB state
      await runTransaction(firestore, async (tx) => {
        const snap = await tx.get(studentAttendanceDocRef);
        if (snap.exists()) {
          tx.delete(studentAttendanceDocRef);
        } else {
          tx.set(studentAttendanceDocRef, { timestamp: serverTimestamp() });
        }
      });
      // Snapshot listener will update UI
      refreshFilteredStudents();
    } catch (error) {
      console.error("Error toggling attendance:", error);
    }
  };

  // Clicking a chip: with lunch on, open the picker (new check-in, or edit for an
  // already-present student); otherwise fall back to the plain present/absent toggle.
  const handleChipClick = (student, isPresent) => {
    if (lunchEnabled) {
      setLunchPopup({ student, editing: isPresent, initial: isPresent ? student.lunch : null });
    } else {
      markAttendance(student.id);
    }
  };

  const studentLunchRef = (sid) =>
    doc(firestore, "classes", selectedClassId, "attendance", todayKeyLocal(), "students", sid);

  // Save a lunch choice. Grade is snapshotted onto the record so the school-wide
  // Lunch counts view can group by grade without re-joining the roster.
  const saveLunch = async (student, lunch, editing) => {
    if (!selectedClassId) return;
    const lunchData = { entree: lunch.entree || "", drink: lunch.drink || "", grade: studentGrade(student) || "" };
    try {
      if (editing) {
        await updateDoc(studentLunchRef(student.id), { lunch: lunchData });
      } else {
        await setDoc(studentLunchRef(student.id), { timestamp: serverTimestamp(), lunch: lunchData });
      }
      refreshFilteredStudents();
    } catch (error) {
      console.error("Error saving lunch choice:", error);
    }
    setLunchPopup(null);
  };

  const removeAttendance = async (sid) => {
    try {
      await deleteDoc(studentLunchRef(sid));
      refreshFilteredStudents();
    } catch (error) {
      console.error("Error removing attendance:", error);
    }
    setLunchPopup(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const options = { hour: "numeric", minute: "numeric" };
    return new Date(timestamp).toLocaleString(undefined, options);
    // If you ever store Firestore Timestamps here directly, convert with toMillis() first.
  };

  const sortStudentsByLastName = (students) => {
    return [...students].sort(byLastName);
  };

  const sortedNotPresentStudents = sortStudentsByLastName(notPresentStudents);
  const sortedPresentStudents = sortStudentsByLastName(presentStudents);
  const total = sortedNotPresentStudents.length + sortedPresentStudents.length;

  return (
    <div className="attendance-container">
      {showModeToggle && (
        <div style={{ display: "flex", gap: 8, marginBottom: "var(--space-3)", flexWrap: "wrap" }}>
          <button className={effectiveMode === "class" ? "" : "btn-secondary"} onClick={() => setMode("class")}>By class</button>
          {isAdmin && (
            <button className={effectiveMode === "absent" ? "" : "btn-secondary"} onClick={() => setMode("absent")}>Absent — school-wide</button>
          )}
          {showLunchMode && (
            <button className={effectiveMode === "lunch" ? "" : "btn-secondary"} onClick={() => setMode("lunch")}>Lunch counts</button>
          )}
        </div>
      )}

      {effectiveMode === "absent" ? (
        <SchoolAbsentView />
      ) : effectiveMode === "lunch" ? (
        <LunchCounts ownClasses={isAdmin ? undefined : ownClasses} />
      ) : (
      <>
      <div className="att-header">
        <h2 style={{ margin: 0 }}>Mark Attendance for Today</h2>
        {total > 0 && (
          <span className="att-summary">
            {sortedPresentStudents.length} / {total} here
          </span>
        )}
      </div>

      {/* Class Selection */}
      <div>
        {dropdownClasses.length > 1 ? (
          <select
            className="class-select"
            value={selectedClassId}
            onChange={handleClassChange}
          >
            {isAdmin && otherClasses.length > 0 ? (
              <>
                {ownClasses.length > 0 && (
                  <optgroup label="My classes">
                    {ownClasses.map((cls) => (
                      <option key={cls.id} value={cls.id}>{cls.className}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="School">
                  {otherClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>{cls.className}</option>
                  ))}
                </optgroup>
              </>
            ) : (
              dropdownClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>{cls.className}</option>
              ))
            )}
          </select>
        ) : (
          <span>{dropdownClasses[0]?.className || "No classes available"}</span>
        )}
      </div>

      {/* One continuous roster: not-here names float to the top, checked-in
          names sink below a divider (de-emphasized) so the full class stays
          visible for verification and tap-to-undo. */}
      {total === 0 ? (
        <p className="att-hint" style={{ marginTop: "var(--space-5)" }}>
          No students in this class yet.
        </p>
      ) : (
        <>
          <div className="att-grid att-section">
            {sortedNotPresentStudents.length === 0 && (
              <div className="att-allhere">Everyone's here 🎉</div>
            )}

            {sortedNotPresentStudents.map((student) => {
              const name = student.studentName || "Unnamed Student";
              return (
                <button
                  key={student.id}
                  className="att-chip att-chip--out"
                  onClick={() => handleChipClick(student, false)}
                >
                  <span className="att-avatar" style={{ background: avatarColor(name) }}>
                    {initials(name)}
                  </span>
                  <span className="att-chip-name">{name}</span>
                  <span className="att-check" aria-hidden="true" />
                </button>
              );
            })}

            {sortedPresentStudents.length > 0 && (
              <div className="att-divider">
                <span>Checked in · {sortedPresentStudents.length}</span>
              </div>
            )}

            {sortedPresentStudents.map((student) => {
              const name = student.studentName || "Unnamed Student";
              return (
                <button
                  key={student.id}
                  className="att-chip att-chip--in"
                  onClick={() => handleChipClick(student, true)}
                >
                  <span className="att-avatar att-avatar--in">{initials(name)}</span>
                  <span className="att-chip-name">
                    {name}
                    {student.timestamp && (
                      <span className="att-time">{formatTimestamp(student.timestamp)}</span>
                    )}
                    {lunchEnabled && student.lunch && lunchSummary(student.lunch) && (
                      <span className="att-lunch">🍽 {lunchSummary(student.lunch)}</span>
                    )}
                  </span>
                  <span className="att-check att-check--done" aria-hidden="true">✓</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      </>
      )}

      {lunchPopup && (
        <LunchPicker
          studentName={lunchPopup.student.studentName || "Student"}
          entrees={lunchEntrees}
          drinks={lunchDrinks}
          initial={lunchPopup.initial}
          editing={lunchPopup.editing}
          onSave={(lunch) => saveLunch(lunchPopup.student, lunch, lunchPopup.editing)}
          onRemove={() => removeAttendance(lunchPopup.student.id)}
          onCancel={() => setLunchPopup(null)}
        />
      )}
    </div>
  );
};

export default Attendance;
